import { TargetVirtualizedItem } from "./types";

interface SizeInfo {
    width: number;

    height: number;
}

interface VirtualizedTableStoreProps<T> {
    getKey(data: T): string;
}

interface RowMeasurement {
    index: number;

    height: number;
}

const EstimatedRowHeight = 82;
const OverScan = 5;
const RenderBatchSize = 5;

class HeightIndex {
    private values: number[] = [];

    private tree: number[] = [0];

    private totalValue = 0;

    reset(values: number[]) {
        this.values = values.slice();
        this.tree = new Array(values.length + 1).fill(0);
        this.totalValue = 0;

        for (let i = 1; i <= values.length; i++) {
            this.tree[i] += values[i - 1];
            this.totalValue += values[i - 1];
            const parent = i + (i & -i);

            if (parent <= values.length) {
                this.tree[parent] += this.tree[i];
            }
        }
    }

    get(index: number) {
        return this.values[index] || 0;
    }

    update(index: number, value: number) {
        const previousValue = this.values[index];

        if (previousValue === undefined || previousValue === value) {
            return;
        }

        const change = value - previousValue;
        this.values[index] = value;
        this.totalValue += change;

        for (let i = index + 1; i < this.tree.length; i += i & -i) {
            this.tree[i] += change;
        }
    }

    prefixSum(end: number) {
        let sum = 0;

        for (let i = Math.min(Math.max(0, end), this.values.length); i > 0; i -= i & -i) {
            sum += this.tree[i];
        }

        return sum;
    }

    get total() {
        return this.totalValue;
    }

    findIndexByOffset(offset: number) {
        if (!this.values.length || offset <= 0) {
            return 0;
        }

        let index = 0;
        let sum = 0;
        let step = 1;

        while (step * 2 <= this.values.length) {
            step *= 2;
        }

        for (; step > 0; step = Math.floor(step / 2)) {
            const nextIndex = index + step;

            if (nextIndex <= this.values.length && sum + this.tree[nextIndex] <= offset) {
                index = nextIndex;
                sum += this.tree[nextIndex];
            }
        }

        return Math.min(index, this.values.length - 1);
    }
}

class VirtualizedTableStore<T> {
    getKey: (data: T) => string;

    private listeners = new Set<() => void>();

    private version = 0;

    private heightMapping = new Map<string, number>();

    private heightIndex = new HeightIndex();

    constructor(props: VirtualizedTableStoreProps<T>) {
        this.getKey = props.getKey;
    }

    subscribe = (listener: () => void) => {
        this.listeners.add(listener);

        return () => {
            this.listeners.delete(listener);
        };
    };

    getSnapshot = () => this.version;

    /** 通知 useSyncExternalStore 重新读取当前渲染窗口。 */
    private emitChange() {
        this.version += 1;
        this.listeners.forEach((listener) => listener());
    }

    data: T[] = [];

    onDataChange = (data: T[]) => {
        const isSameData = this.data.length === data.length && this.data.every((item, index) => item === data[index]);

        if (isSameData) {
            return;
        }

        // 搜索结果变化时保留仍存在订单的测量值，并清理失效缓存。
        const validKeys = new Set(data.map((item) => this.getKey(item)));
        this.heightMapping.forEach((_, key) => {
            if (!validKeys.has(key)) {
                this.heightMapping.delete(key);
            }
        });

        this.data = data.slice();
        this.heightIndex.reset(data.map((item) => this.heightMapping.get(this.getKey(item)) ?? EstimatedRowHeight));
        this.emitChange();
    };

    scrollTop = 0;

    containerSize: SizeInfo = {
        width: 0,
        height: 0,
    };

    tableBoundingTop = 0;

    onScrollTopChange = (scrollTop: number) => {
        if (this.scrollTop === scrollTop) {
            return;
        }

        const previousRange = this.getRenderRange(this.scrollTop);
        this.scrollTop = scrollTop;
        const nextRange = this.getRenderRange(this.scrollTop);

        // 仅在跨出当前懒渲染窗口时更新 React，普通滚动只记录位置。
        if (previousRange.start !== nextRange.start || previousRange.end !== nextRange.end) {
            this.emitChange();
        }
    };

    handleContainerSizeChange = (size: SizeInfo) => {
        if (this.containerSize.width === size.width && this.containerSize.height === size.height) {
            return;
        }

        this.containerSize = size;
        this.emitChange();
    };

    onTableBoundingTop = (top: number) => {
        if (this.tableBoundingTop === top) {
            return;
        }

        this.tableBoundingTop = top;
        this.emitChange();
    };

    getOffsetTopByIndex(index: number) {
        return this.heightIndex.prefixSum(index);
    }

    private getIndexByOffset(offsetTop: number) {
        return this.heightIndex.findIndexByOffset(offsetTop);
    }

    /**
     * 根据滚动位置计算可见锚点和懒渲染区间。
     *
     * 渲染窗口按批次移动，避免每跨过一条订单就让整张表重新渲染；
     * 可见区域上下仍保留 OverScan 条，确保批次切换前已有足够缓冲。
     */
    private getRenderRange(scrollTop: number) {
        if (!this.data.length) {
            return { anchor: 0, start: 0, end: 0 };
        }

        const realScrollTop = Math.max(0, scrollTop - this.tableBoundingTop);
        const anchor = this.getIndexByOffset(realScrollTop);
        const batchStart = Math.floor(anchor / RenderBatchSize) * RenderBatchSize;
        const start = Math.max(0, batchStart - OverScan);
        const viewportBottom = realScrollTop + this.containerSize.height;
        const visibleEnd = Math.min(this.data.length, this.getIndexByOffset(viewportBottom) + 1);
        const batchEnd = Math.ceil(visibleEnd / RenderBatchSize) * RenderBatchSize;

        return {
            anchor,
            start,
            end: Math.min(this.data.length, batchEnd + OverScan),
        };
    }

    /**
     * 批量写入新渲染窗口的真实行高。
     *
     * 行高变化会改变顶部占位高度，因此同步补偿锚点上方的高度差，
     * 保持用户当前看到的首行不发生位移。
     */
    measureRows(measurements: RowMeasurement[]) {
        if (!measurements.length || !this.data.length) {
            return;
        }

        const { anchor } = this.getRenderRange(this.scrollTop);
        const previousAnchorTop = this.getOffsetTopByIndex(anchor);
        let hasChange = false;

        measurements.forEach(({ index, height }) => {
            const item = this.data[index];
            const nextHeight = Math.round(height);

            if (!item || !nextHeight) {
                return;
            }

            const key = this.getKey(item);
            const previousHeight = this.heightMapping.get(key);

            if (previousHeight === nextHeight) {
                return;
            }

            this.heightMapping.set(key, nextHeight);
            this.heightIndex.update(index, nextHeight);
            hasChange = true;
        });

        if (!hasChange) {
            return;
        }

        const nextAnchorTop = this.getOffsetTopByIndex(anchor);
        const anchorOffsetChange = nextAnchorTop - previousAnchorTop;

        if (anchorOffsetChange) {
            this.scrollTop += anchorOffsetChange;
            window.scrollBy({ top: anchorOffsetChange });
        }

        this.emitChange();
    }

    estimateOffsetTopByIndex(index: number): number {
        if (index < 0 || index >= this.data.length) {
            return 0;
        }

        return this.tableBoundingTop + this.getOffsetTopByIndex(index);
    }

    scrollToIndex(index: number) {
        const estimatedOffset = this.estimateOffsetTopByIndex(index);

        this.scrollTop = estimatedOffset;
        this.emitChange();
        window.scrollTo({ top: estimatedOffset });

        return estimatedOffset;
    }

    get containerContentHeight() {
        return this.heightIndex.total;
    }

    get renderResult(): {
        start: number;
        end: number;
        marginTop: number;
        list: TargetVirtualizedItem<T>[];
        marginBottom: number;
    } {
        const { start, end } = this.getRenderRange(this.scrollTop);
        const marginTop = this.getOffsetTopByIndex(start);
        const endTop = this.getOffsetTopByIndex(end);

        return {
            start,
            end,
            // 未渲染订单由上下两个占位行保留其估算或已测量高度。
            marginTop,
            list: this.data.slice(start, end).map((item, index) => ({
                index: start + index,
                key: this.getKey(item),
                data: item,
            })),
            marginBottom: Math.max(0, this.containerContentHeight - endTop),
        };
    }
}

export default VirtualizedTableStore;
